/**
 * Notification navigation helper.
 *
 * Single source of truth for "where does clicking this notification go?"
 * Shared by NotificationItem (bell dropdown), NotificationsPage (full-page
 * table), and NotificationToastProvider (toast "View" action) so the three
 * surfaces can never drift.
 *
 * Destinations are derived from `metadata` (executionId / deviceId / …). A
 * stored `route` field is preferred when present so a future writer can point
 * at a destination that isn't derivable from metadata.
 */

import type { Notification } from './notificationService';

export function getNotificationNavUrl(n: Pick<Notification, 'type' | 'metadata' | 'route'>): string | null {
  // An explicit route wins — lets a writer target a destination metadata can't express.
  if (n.route) return n.route;

  const { type, metadata } = n;

  if (
    type === 'execution_complete' ||
    type === 'execution_failed' ||
    type === 'esim_provisioning_failure'
  ) {
    return metadata.executionId
      ? `/reports/runs/${metadata.executionId}`
      : '/tests?tab=history';
  }

  if (type === 'schedule_triggered') {
    // Scheduling consolidated into /tests (legacy /schedules retired).
    return '/tests';
  }

  if ((type === 'device_offline' || type === 'device_online') && metadata.deviceId) {
    return `/admin/edge-devices?device=${metadata.deviceId}`;
  }

  return null;
}
